import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { api, formatApiError, TOKEN_KEY } from './api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [status, setStatus] = useState('loading');
  const [user, setUser] = useState(null);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get('/auth/me');
      return data;
    } catch {
      return null;
    }
  }, []);

  const bootstrap = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setUser(null);
      setStatus('unauthenticated');
      return;
    }
    const me = await fetchMe();
    if (me) {
      setUser(me);
      setStatus('authenticated');
    } else {
      localStorage.removeItem(TOKEN_KEY);
      setUser(null);
      setStatus('unauthenticated');
    }
  }, [fetchMe]);

  useEffect(() => {
    bootstrap();
  }, [bootstrap]);

  const signIn = useCallback(async (email, password) => {
    try {
      const { data } = await api.post('/auth/login', {
        email: email.trim().toLowerCase(),
        password,
      });
      localStorage.setItem(TOKEN_KEY, data.access_token);
      setUser(data.user);
      setStatus('authenticated');
    } catch (err) {
      throw new Error(formatApiError(err));
    }
  }, []);

  const signUp = useCallback(async (email, password, name) => {
    try {
      const { data } = await api.post('/auth/signup', {
        email: email.trim().toLowerCase(),
        password,
        name: name.trim(),
      });
      localStorage.setItem(TOKEN_KEY, data.access_token);
      setUser(data.user);
      setStatus('authenticated');
    } catch (err) {
      throw new Error(formatApiError(err));
    }
  }, []);

  const signOut = useCallback(async () => {
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
    setStatus('unauthenticated');
  }, []);

  const updateProfile = useCallback(async (patch) => {
    try {
      const { data } = await api.put('/auth/me', patch);
      setUser(data);
      return data;
    } catch (err) {
      throw new Error(formatApiError(err));
    }
  }, []);

  const value = useMemo(
    () => ({ status, user, signIn, signUp, signOut, updateProfile, refresh: bootstrap }),
    [status, user, signIn, signUp, signOut, updateProfile, bootstrap],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
